import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { X, Copy, Download, Check, Grid3x3, ChevronDown, Columns3, Plus, Merge, MousePointerClick, Undo2, Redo2 } from 'lucide-react';

/**
 * 📊 GridTextEditor — 열 분리 + 병합 + 격자형 편집기
 *
 * 핵심 유즈케이스: Description of Goods 같은 열을 구분자로 분리하여
 * 2~3개 열로 나누고, 필요에 따라 다시 병합.
 * 마우스로 사각형 영역 드래그 선택 → 복사/다운로드/적용
 *
 * Props:
 *  - columnName: string
 *  - columnField: string
 *  - values: string[]
 *  - onApply: (splitColumns: {header: string, values: string[]}[]) => void
 *  - onClose: () => void
 */
const GridTextEditor = ({ columnName, columnField, values, onApply, onClose }) => {
    // ─── 구분자 ───
    const DELIMITERS = [
        { char: ',', label: '쉼표 (,)' },
        { char: '\t', label: '탭' },
        { char: '|', label: '파이프 (|)' },
        { char: ';', label: '세미콜론 (;)' },
        { char: ' ', label: '공백' },
    ];

    const detectDelimiter = useCallback((vals) => {
        const sample = vals.slice(0, 10).filter(v => v.length > 0);
        if (sample.length === 0) return ',';
        let bestDelim = ',';
        let bestScore = 0;
        for (const d of DELIMITERS) {
            const counts = sample.map(v => v.split(d.char).length);
            const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
            if (avg <= 1) continue;
            const variance = counts.reduce((s, c) => s + Math.pow(c - avg, 2), 0) / counts.length;
            const score = avg / (1 + variance * 0.5);
            if (score > bestScore) { bestScore = score; bestDelim = d.char; }
        }
        return bestDelim;
    }, []);

    const [delimiter, setDelimiter] = useState(() => detectDelimiter(values));
    const [showDelimDropdown, setShowDelimDropdown] = useState(false);
    const [customDelim, setCustomDelim] = useState('');

    // ─── 분리된 데이터 ───
    const splitData = useMemo(() => {
        const rows = values.map(v => v.split(delimiter));
        const maxCols = rows.reduce((max, row) => Math.max(max, row.length), 0);
        return rows.map(row => {
            const padded = [...row];
            while (padded.length < maxCols) padded.push('');
            return padded.map(cell => cell.trim());
        });
    }, [values, delimiter]);

    // ─── 편집 가능 셀 데이터 ───
    const [cells, setCells] = useState(splitData);
    const [colLabels, setColLabels] = useState([]); // 각 열의 라벨 (병합 시 업데이트)
    useEffect(() => {
        setCells(splitData);
        const cc = splitData.length > 0 ? splitData[0].length : 0;
        setColLabels(Array.from({ length: cc }, (_, i) => i === 0 ? (columnName || 'A') : `분리 ${i}`));
        setMergeCols(new Set());
    }, [splitData, columnName]);

    const colCount = cells.length > 0 ? cells[0].length : 0;

    // ─── 병합할 열 선택 (Ctrl/Shift 클릭) ───
    const [mergeCols, setMergeCols] = useState(new Set());
    const [lastClickedCol, setLastClickedCol] = useState(-1);
    const [mergeSeparator, setMergeSeparator] = useState(' ');
    const [showMergeSepDropdown, setShowMergeSepDropdown] = useState(false);

    const MERGE_SEPARATORS = [
        { char: ' ', label: '공백' },
        { char: ',', label: '쉼표 (,)' },
        { char: ', ', label: '쉼표+공백' },
        { char: '-', label: '하이픈 (-)' },
        { char: '', label: '없음 (직접 연결)' },
    ];

    // ─── Selection (드래그) ───
    const [selStart, setSelStart] = useState(null);
    const [selEnd, setSelEnd] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // ─── 행별 병합 모드 ───
    const [rowMergeMode, setRowMergeMode] = useState(false);
    const [rowStartCols, setRowStartCols] = useState(new Map()); // Map<rowIndex, colIndex>

    // ─── Editing ───
    const [editCell, setEditCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef(null);

    // ─── 키보드 포커스 셀 ───
    const [focusCell, setFocusCell] = useState(null); // {r, c}
    const tableContainerRef = useRef(null);

    // ─── Undo/Redo ───
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    // ─── IME ───
    const isComposingRef = useRef(false);

    // ─── 가상 스크롤 ───
    const ROW_HEIGHT = 26;
    const BUFFER_ROWS = 10;
    const scrollContainerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const visibleRange = useMemo(() => {
        const containerH = scrollContainerRef.current?.clientHeight || 500;
        const start = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS);
        const end = Math.min(cells.length - 1, Math.ceil((scrollTop + containerH) / ROW_HEIGHT) + BUFFER_ROWS);
        return { start, end };
    }, [scrollTop, cells.length]);

    // ─── Feedback ───
    const [toastMsg, setToastMsg] = useState('');
    const toastTimer = useRef(null);

    // ─── 선택 범위 ───
    const selBounds = useMemo(() => {
        if (!selStart || !selEnd) return null;
        return {
            r1: Math.min(selStart.r, selEnd.r), r2: Math.max(selStart.r, selEnd.r),
            c1: Math.min(selStart.c, selEnd.c), c2: Math.max(selStart.c, selEnd.c),
        };
    }, [selStart, selEnd]);

    const isSelected = useCallback((r, c) => {
        if (!selBounds) return false;
        return r >= selBounds.r1 && r <= selBounds.r2 && c >= selBounds.c1 && c <= selBounds.c2;
    }, [selBounds]);

    const selSize = useMemo(() => {
        if (!selBounds) return null;
        return { rows: selBounds.r2 - selBounds.r1 + 1, cols: selBounds.c2 - selBounds.c1 + 1 };
    }, [selBounds]);

    // ─── 마우스 이벤트 (셀 드래그 선택) ───
    const handleMouseDown = useCallback((r, c, e) => {
        if (editCell) return;
        e.preventDefault();

        // 🔶 행별 병합 모드: 클릭한 셀을 해당 행의 시작 위치로 마킹
        if (rowMergeMode) {
            setRowStartCols(prev => {
                const next = new Map(prev);
                if (next.get(r) === c) {
                    next.delete(r); // 같은 셀 재클릭 → 해제
                } else {
                    next.set(r, c); // 시작 위치 마킹
                }
                return next;
            });
            return;
        }

        setFocusCell({ r, c });
        setSelStart({ r, c });
        setSelEnd({ r, c });
        setIsDragging(true);
    }, [editCell, rowMergeMode]);

    const handleMouseEnter = useCallback((r, c) => {
        if (!isDragging) return;
        setSelEnd({ r, c });
    }, [isDragging]);

    useEffect(() => {
        const up = () => { if (isDragging) setIsDragging(false); };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, [isDragging]);

    // ─── 열 헤더 클릭 (Ctrl/Shift로 병합 열 선택) ───
    const handleColHeaderClick = useCallback((ci, e) => {
        if (e.ctrlKey || e.metaKey) {
            // Ctrl+클릭: 토글
            setMergeCols(prev => {
                const next = new Set(prev);
                if (next.has(ci)) next.delete(ci);
                else next.add(ci);
                return next;
            });
            setLastClickedCol(ci);
            // Ctrl 클릭 시에는 드래그 선택 영역을 해제하거나 유지 (병합 대상만 변경)
        } else if (e.shiftKey && lastClickedCol >= 0) {
            // Shift+클릭: 범위 선택 (전체 열 선택)
            const from = Math.min(lastClickedCol, ci);
            const to = Math.max(lastClickedCol, ci);

            // mergeCols 업데이트 (범위 내 모든 열)
            const newMergeCols = new Set();
            for (let i = from; i <= to; i++) newMergeCols.add(i);
            setMergeCols(newMergeCols);

            // 시각적 선택 영역 업데이트
            setSelStart({ r: 0, c: from });
            setSelEnd({ r: cells.length - 1, c: to });
        } else {
            // 일반 클릭: 해당 열만 선택 (기존 선택 초기화)
            setMergeCols(new Set([ci]));
            setLastClickedCol(ci);

            // 전체 열 선택
            setSelStart({ r: 0, c: ci });
            setSelEnd({ r: cells.length - 1, c: ci });
        }
    }, [lastClickedCol, cells.length]);

    // ─── 더블클릭 편집 ───
    const handleDoubleClick = useCallback((r, c) => {
        setEditCell({ r, c });
        setEditValue(cells[r][c]);
        setFocusCell(null);
    }, [cells]);

    useEffect(() => {
        if (editCell && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editCell]);

    const commitEdit = useCallback((newVal) => {
        if (!editCell) return;
        const val = newVal !== undefined ? newVal : editValue;
        const before = cells[editCell.r]?.[editCell.c] || '';
        if (before !== val) {
            setCells(prev => {
                const next = prev.map(row => [...row]);
                next[editCell.r][editCell.c] = val;
                return next;
            });
            setUndoStack(prev => [...prev, { type: 'cell', r: editCell.r, c: editCell.c, before, after: val }]);
            setRedoStack([]);
        }
        setFocusCell({ r: editCell.r, c: editCell.c });
        setEditCell(null);
    }, [editCell, editValue, cells]);

    // ─── 토스트 ───
    const showToast = useCallback((msg) => {
        setToastMsg(msg);
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToastMsg(''), 2500);
    }, []);

    // ─── Undo/Redo ───
    const performUndo = useCallback(() => {
        setUndoStack(prev => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            const rest = prev.slice(0, -1);
            if (last.type === 'cell') {
                setCells(c => { const n = c.map(r => [...r]); n[last.r][last.c] = last.before; return n; });
                setRedoStack(rs => [...rs, last]);
                setFocusCell({ r: last.r, c: last.c });
            } else if (last.type === 'snapshot') {
                setCells(c => { setRedoStack(rs => [...rs, { type: 'snapshot', data: c.map(r => [...r]), labels: [...(colLabels)] }]); return last.data; });
                if (last.labels) setColLabels(last.labels);
            }
            showToast(`↩ 되돌리기 (${rest.length}개 남음)`);
            return rest;
        });
    }, [colLabels, showToast]);

    const performRedo = useCallback(() => {
        setRedoStack(prev => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            const rest = prev.slice(0, -1);
            if (last.type === 'cell') {
                setCells(c => { const n = c.map(r => [...r]); n[last.r][last.c] = last.after; return n; });
                setUndoStack(us => [...us, last]);
                setFocusCell({ r: last.r, c: last.c });
            } else if (last.type === 'snapshot') {
                setCells(c => { setUndoStack(us => [...us, { type: 'snapshot', data: c.map(r => [...r]), labels: [...(colLabels)] }]); return last.data; });
                if (last.labels) setColLabels(last.labels);
            }
            showToast(`↪ 다시 실행`);
            return rest;
        });
    }, [colLabels, showToast]);

    // ─── Undo 스냅샷 헬퍼 (병합/분리 전 호출) ───
    const pushSnapshot = useCallback(() => {
        setUndoStack(prev => [...prev, { type: 'snapshot', data: cells.map(r => [...r]), labels: [...colLabels] }]);
        setRedoStack([]);
    }, [cells, colLabels]);

    const handleEditKeyDown = useCallback((e) => {
        if (isComposingRef.current) return; // IME 조합 중 무시
        if (e.key === 'Enter') {
            e.preventDefault();
            commitEdit();
            if (editCell && editCell.r < cells.length - 1) {
                const nr = editCell.r + 1, nc = editCell.c;
                setEditCell({ r: nr, c: nc });
                setEditValue(cells[nr][nc]);
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            commitEdit();
            if (editCell) {
                const nc = e.shiftKey ? Math.max(0, editCell.c - 1) : Math.min(colCount - 1, editCell.c + 1);
                setEditCell({ r: editCell.r, c: nc });
                setEditValue(cells[editCell.r][nc]);
            }
        } else if (e.key === 'Escape') {
            setFocusCell(editCell ? { r: editCell.r, c: editCell.c } : null);
            setEditCell(null);
        }
    }, [commitEdit, editCell, cells, colCount]);



    // ─── 복사 ───
    const copySelection = useCallback(() => {
        if (!selBounds) { showToast('영역을 먼저 선택하세요'); return; }
        const lines = [];
        for (let r = selBounds.r1; r <= selBounds.r2; r++) {
            const rowData = [];
            for (let c = selBounds.c1; c <= selBounds.c2; c++) rowData.push(cells[r][c]);
            lines.push(rowData.join('\t'));
        }
        navigator.clipboard.writeText(lines.join('\n'));
        showToast(`✅ ${selSize.rows}행 × ${selSize.cols}열 복사됨`);
    }, [selBounds, selSize, cells, showToast]);

    // ─── 다운로드 ───
    const downloadSelection = useCallback(() => {
        if (!selBounds) { showToast('영역을 먼저 선택하세요'); return; }
        const hdr = colLabels.slice(selBounds.c1, selBounds.c2 + 1);
        const lines = [hdr.join(',')];
        for (let r = selBounds.r1; r <= selBounds.r2; r++) {
            const rowData = [];
            for (let c = selBounds.c1; c <= selBounds.c2; c++) {
                let v = cells[r][c];
                if (v.includes(',') || v.includes('"') || v.includes('\n')) v = '"' + v.replace(/"/g, '""') + '"';
                rowData.push(v);
            }
            lines.push(rowData.join(','));
        }
        const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${columnName || 'data'}_split_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showToast(`📥 CSV 다운로드 (${selSize.rows}행 × ${selSize.cols}열)`);
    }, [selBounds, selSize, cells, colLabels, columnName, showToast]);

    // ─── 키보드 핸들러 (Ctrl+C, Ctrl+Z/Y, Arrow 키) ───
    useEffect(() => {
        const handler = (e) => {
            if (editCell) return; // 편집 중에는 무시
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                e.preventDefault();
                copySelection();
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                performUndo();
            } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
                e.preventDefault();
                performRedo();
            } else if (focusCell && !editCell) {
                const { r, c } = focusCell;
                if (e.key === 'ArrowUp') { e.preventDefault(); setFocusCell({ r: Math.max(0, r - 1), c }); setSelStart(null); setSelEnd(null); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); setFocusCell({ r: Math.min(cells.length - 1, r + 1), c }); setSelStart(null); setSelEnd(null); }
                else if (e.key === 'ArrowLeft') { e.preventDefault(); setFocusCell({ r, c: Math.max(0, c - 1) }); setSelStart(null); setSelEnd(null); }
                else if (e.key === 'ArrowRight') { e.preventDefault(); setFocusCell({ r, c: Math.min(colCount - 1, c + 1) }); setSelStart(null); setSelEnd(null); }
                else if (e.key === 'Tab') {
                    e.preventDefault();
                    const nc = e.shiftKey ? Math.max(0, c - 1) : Math.min(colCount - 1, c + 1);
                    setFocusCell({ r, c: nc }); setSelStart(null); setSelEnd(null);
                }
                else if (e.key === 'Enter') { e.preventDefault(); setEditCell({ r, c }); setEditValue(cells[r][c]); }
                else if (e.key === 'Delete' || e.key === 'Backspace') {
                    e.preventDefault();
                    const before = cells[r][c];
                    if (before) {
                        setCells(prev => { const n = prev.map(row => [...row]); n[r][c] = ''; return n; });
                        setUndoStack(prev => [...prev, { type: 'cell', r, c, before, after: '' }]);
                        setRedoStack([]);
                    }
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [copySelection, editCell, focusCell, cells, colCount, performUndo, performRedo]);

    // ─── 🔀 열 병합 ───
    const mergeSelectedCols = useCallback(() => {
        if (mergeCols.size < 2) { showToast('병합할 열을 2개 이상 선택하세요 (Ctrl+클릭)'); return; }
        pushSnapshot();

        const sorted = [...mergeCols].sort((a, b) => a - b);
        const firstCol = sorted[0];

        setCells(prev => {
            return prev.map(row => {
                // 선택된 열의 값을 합치기 (빈 값 제외)
                const mergedVal = sorted
                    .map(ci => (row[ci] || '').trim())
                    .filter(v => v.length > 0)
                    .join(mergeSeparator);

                // 새 행: 병합 대상이 아닌 열만 유지, 첫 번째 열은 병합 결과로 대체
                const newRow = [];
                for (let ci = 0; ci < row.length; ci++) {
                    if (ci === firstCol) {
                        newRow.push(mergedVal);
                    } else if (!sorted.includes(ci) || ci === firstCol) {
                        newRow.push(row[ci]);
                    }
                    // sorted에 포함된 ci(firstCol 제외)는 건너뜀 → 열 제거
                }
                return newRow;
            });
        });

        // 라벨 업데이트
        setColLabels(prev => {
            const mergedLabel = sorted.map(ci => prev[ci] || String.fromCharCode(65 + ci)).join('+');
            const newLabels = [];
            for (let ci = 0; ci < prev.length; ci++) {
                if (ci === firstCol) newLabels.push(mergedLabel);
                else if (!sorted.includes(ci)) newLabels.push(prev[ci]);
            }
            return newLabels;
        });

        setMergeCols(new Set());
        setSelStart(null);
        setSelEnd(null);
        showToast(`✅ ${sorted.length}개 열 병합 완료 → "${sorted.map(ci => colLabels[ci] || String.fromCharCode(65 + ci)).join(' + ')}"`);
    }, [mergeCols, mergeSeparator, colLabels, showToast]);

    // ─── ✂️ 선택된 열 분리 (1개 열을 구분자로 재분리) ───
    const splitSelectedCol = useCallback(() => {
        if (mergeCols.size !== 1) { showToast('분리할 열을 1개만 선택하세요 (Ctrl+클릭)'); return; }
        const targetCol = [...mergeCols][0];
        const splitDelim = mergeSeparator || delimiter;
        pushSnapshot();

        // 해당 열의 값들을 구분자로 분리
        const splitParts = cells.map(row => {
            const val = row[targetCol] || '';
            const parts = val.split(splitDelim).map(s => s.trim());
            return parts.length > 1 ? parts : [val, ''];
        });
        const maxNewCols = splitParts.reduce((max, p) => Math.max(max, p.length), 0);

        if (maxNewCols <= 1) {
            showToast(`⚠️ 구분자 "${MERGE_SEPARATORS.find(s => s.char === splitDelim)?.label || splitDelim}"로 분리할 데이터가 없습니다`);
            return;
        }

        // 셀 데이터 업데이트: targetCol 위치에 분리된 열들 삽입
        setCells(prev => prev.map((row, ri) => {
            const newRow = [];
            for (let ci = 0; ci < row.length; ci++) {
                if (ci === targetCol) {
                    const parts = splitParts[ri];
                    for (let pi = 0; pi < maxNewCols; pi++) {
                        newRow.push(parts[pi] || '');
                    }
                } else {
                    newRow.push(row[ci]);
                }
            }
            return newRow;
        }));

        // 라벨 업데이트
        setColLabels(prev => {
            const baseLabel = prev[targetCol] || String.fromCharCode(65 + targetCol);
            const newLabels = [];
            for (let ci = 0; ci < prev.length; ci++) {
                if (ci === targetCol) {
                    for (let pi = 0; pi < maxNewCols; pi++) {
                        newLabels.push(pi === 0 ? baseLabel : `${baseLabel}.${pi}`);
                    }
                } else {
                    newLabels.push(prev[ci]);
                }
            }
            return newLabels;
        });

        setMergeCols(new Set());
        setSelStart(null);
        setSelEnd(null);
        showToast(`✅ 열 분리 완료 → ${maxNewCols}개 열로 분리됨`);
    }, [mergeCols, mergeSeparator, delimiter, cells, colLabels, showToast]);

    // ─── 드래그 선택 영역 병합 ───
    const mergeSelectionCols = useCallback(() => {
        if (!selBounds || selBounds.c1 === selBounds.c2) {
            showToast('병합할 열 범위를 드래그로 선택하세요 (2열 이상)');
            return;
        }
        // 선택된 열 범위를 mergeCols에 설정하고 병합
        const cols = new Set();
        for (let c = selBounds.c1; c <= selBounds.c2; c++) cols.add(c);
        setMergeCols(cols);
        // 바로 병합 실행을 위해 setTimeout 사용 (state 업데이트 후)
    }, [selBounds, showToast]);

    // ─── 🔶 행별 병합 적용 ───
    const applyRowMerge = useCallback(() => {
        if (rowStartCols.size === 0) {
            showToast('⚠️ 행별 시작 위치를 먼저 지정하세요');
            return;
        }
        pushSnapshot();

        setCells(prev => {
            // 1단계: 행별 병합 수행
            const merged = prev.map((row, ri) => {
                const startCol = rowStartCols.get(ri);
                if (startCol === undefined || startCol >= row.length) return [...row];

                // 시작 열부터 끝까지의 값을 병합
                const mergedVal = row.slice(startCol)
                    .map(v => (v || '').trim())
                    .filter(v => v.length > 0)
                    .join(mergeSeparator);

                const newRow = [...row];
                newRow[startCol] = mergedVal;
                // 시작 열 이후는 빈 문자열
                for (let ci = startCol + 1; ci < newRow.length; ci++) {
                    newRow[ci] = '';
                }
                return newRow;
            });

            // 2단계: 오른쪽부터 완전히 빈 열 제거
            if (merged.length > 0) {
                const totalCols = merged[0].length;
                let lastNonEmptyCol = 0; // 최소 1열은 유지
                for (let ci = totalCols - 1; ci >= 0; ci--) {
                    const hasData = merged.some(row => (row[ci] || '').trim().length > 0);
                    if (hasData) {
                        lastNonEmptyCol = ci;
                        break;
                    }
                }
                const keepCols = lastNonEmptyCol + 1;
                if (keepCols < totalCols) {
                    const trimmed = merged.map(row => row.slice(0, keepCols));
                    // 라벨도 같이 자르기
                    setColLabels(prev => prev.slice(0, keepCols));
                    console.log(`🧹 [행별 병합] 빈 열 ${totalCols - keepCols}개 제거 → ${keepCols}열`);
                    return trimmed;
                }
            }
            return merged;
        });

        showToast(`✅ ${rowStartCols.size}개 행 병합 완료 (빈 열 자동 제거)`);
        setRowStartCols(new Map());
        setRowMergeMode(false);
    }, [rowStartCols, mergeSeparator, showToast, pushSnapshot]);

    // ─── 🔶 행별 병합: 전체 행에 같은 시작 열 일괄 지정 ───
    const setAllRowStarts = useCallback((colIdx) => {
        const next = new Map();
        for (let r = 0; r < cells.length; r++) {
            if (colIdx < cells[r].length) {
                next.set(r, colIdx);
            }
        }
        setRowStartCols(next);
        showToast(`✅ 전체 ${cells.length}행의 시작 위치를 ${String.fromCharCode(65 + colIdx)}열로 설정`);
    }, [cells, showToast]);

    // ─── 🔶 행별 병합 모드에서 셀이 하이라이트인지 확인 ───
    const isRowMergeHighlighted = useCallback((ri, ci) => {
        const startCol = rowStartCols.get(ri);
        if (startCol === undefined) return false;
        return ci >= startCol;
    }, [rowStartCols]);

    const isRowMergeStart = useCallback((ri, ci) => {
        return rowStartCols.get(ri) === ci;
    }, [rowStartCols]);

    // mergeCols가 2개 이상이면서 mergeSelectionCols에서 세팅된 직후 → 자동 병합 안 함 (사용자가 병합 버튼 클릭)

    // ─── 전체 선택 ───
    const selectAll = useCallback(() => {
        if (cells.length > 0 && colCount > 0) {
            setSelStart({ r: 0, c: 0 });
            setSelEnd({ r: cells.length - 1, c: colCount - 1 });
        }
    }, [cells.length, colCount]);

    // ─── 적용 ───
    const handleApply = useCallback(() => {
        const splitColumns = [];
        for (let c = 0; c < colCount; c++) {
            splitColumns.push({
                header: colLabels[c] || String.fromCharCode(65 + c),
                values: cells.map(row => row[c] || ''),
            });
        }
        onApply(splitColumns);
    }, [cells, colCount, colLabels, onApply]);

    // ─── 병합/분리 가능 여부 ───
    const canMerge = mergeCols.size >= 2;
    const canSplit = mergeCols.size === 1;
    const canRowMerge = rowStartCols.size > 0;

    return (
        <>
            <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
            <div
                className="fixed z-50 bg-white rounded-xl flex flex-col"
                style={{
                    top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 'min(95vw, 1100px)', maxHeight: '85vh',
                    boxShadow: '0 25px 80px rgba(0,0,0,0.35)',
                    border: '1px solid #e2e8f0'
                }}
            >
                {/* ─── 헤더 ─── */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <Columns3 className="w-5 h-5 text-gray-600" />
                        <span className="text-sm font-bold text-gray-800">열 분리 편집기</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{columnName}</span>
                        <span className="text-xs text-gray-400">{cells.length}행 × {colCount}열</span>
                        {undoStack.length > 0 && <span className="text-[10px] text-gray-400">↩{undoStack.length}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        {selSize && (
                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                선택: {selSize.rows}×{selSize.cols}
                            </span>
                        )}
                        <button onClick={performUndo} disabled={undoStack.length === 0}
                            className={`p-1.5 rounded-lg transition-colors ${undoStack.length > 0 ? 'text-gray-600 hover:bg-gray-200' : 'text-gray-300 cursor-not-allowed'}`}
                            title="되돌리기 (Ctrl+Z)"><Undo2 className="w-3.5 h-3.5" /></button>
                        <button onClick={performRedo} disabled={redoStack.length === 0}
                            className={`p-1.5 rounded-lg transition-colors ${redoStack.length > 0 ? 'text-gray-600 hover:bg-gray-200' : 'text-gray-300 cursor-not-allowed'}`}
                            title="다시 실행 (Ctrl+Y)"><Redo2 className="w-3.5 h-3.5" /></button>
                        <button onClick={copySelection}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center gap-1.5"
                            title="선택 영역 복사 (Ctrl+C)">
                            <Copy className="w-3.5 h-3.5" /> 복사
                        </button>
                        <button onClick={downloadSelection}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center gap-1.5"
                            title="선택 영역 CSV 다운로드">
                            <Download className="w-3.5 h-3.5" /> 다운로드
                        </button>
                        <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors ml-1">
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* ─── 도구 바: 구분자 + 병합 ─── */}
                <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                        {/* 구분자 */}
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-500 font-medium">구분자:</span>
                            <div className="relative">
                                <button
                                    onClick={() => setShowDelimDropdown(!showDelimDropdown)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 transition"
                                >
                                    <span>{DELIMITERS.find(d => d.char === delimiter)?.label || `"${delimiter}"`}</span>
                                    <ChevronDown className="w-3 h-3 text-gray-400" />
                                </button>
                                {showDelimDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => setShowDelimDropdown(false)} />
                                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1 min-w-[160px]">
                                            {DELIMITERS.map(d => (
                                                <button key={d.char}
                                                    onClick={() => { setDelimiter(d.char); setShowDelimDropdown(false); }}
                                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 transition ${delimiter === d.char ? 'text-gray-900 font-bold bg-gray-100' : 'text-gray-700'}`}
                                                >{d.label}</button>
                                            ))}
                                            <div className="border-t border-gray-100 mt-1 pt-1 px-3 pb-2">
                                                <div className="text-[10px] text-gray-400 mb-1">직접 입력:</div>
                                                <div className="flex gap-1">
                                                    <input type="text" value={customDelim} onChange={e => setCustomDelim(e.target.value)}
                                                        placeholder="구분자" className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs w-16" maxLength={5} />
                                                    <button onClick={() => { if (customDelim) { setDelimiter(customDelim); setShowDelimDropdown(false); } }}
                                                        className="px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-800">적용</button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 구분선 */}
                        <div className="w-px h-5 bg-gray-300" />

                        {/* 병합 */}
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-500 font-medium">병합:</span>
                            {/* 병합 구분자 선택 */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowMergeSepDropdown(!showMergeSepDropdown)}
                                    className="flex items-center gap-1 px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-xs hover:bg-gray-50 transition"
                                >
                                    <span className="text-gray-500">{MERGE_SEPARATORS.find(s => s.char === mergeSeparator)?.label || `"${mergeSeparator}"`}</span>
                                    <ChevronDown className="w-3 h-3 text-gray-400" />
                                </button>
                                {showMergeSepDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => setShowMergeSepDropdown(false)} />
                                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1 min-w-[140px]">
                                            {MERGE_SEPARATORS.map(s => (
                                                <button key={s.label}
                                                    onClick={() => { setMergeSeparator(s.char); setShowMergeSepDropdown(false); }}
                                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 transition ${mergeSeparator === s.char ? 'text-gray-900 font-bold bg-gray-100' : 'text-gray-700'}`}
                                                >{s.label}</button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            <button
                                onClick={mergeSelectedCols}
                                disabled={!canMerge}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 ${canMerge
                                    ? 'bg-gray-700 text-white hover:bg-gray-800'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                title={canMerge ? `${mergeCols.size}개 열 병합` : 'Ctrl+클릭으로 열 헤더를 2개 이상 선택하세요'}
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                병합 {canMerge ? `(${mergeCols.size}열)` : ''}
                            </button>

                            {/* 구분선 */}
                            <div className="w-px h-5 bg-gray-300" />

                            {/* 분리 버튼 */}
                            <button
                                onClick={splitSelectedCol}
                                disabled={!canSplit}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 ${canSplit
                                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                title={canSplit ? '선택된 열을 구분자로 다시 분리' : 'Ctrl+클릭으로 열 헤더를 1개 선택하세요'}
                            >
                                <Grid3x3 className="w-3.5 h-3.5" />
                                분리 {canSplit ? '(1열)' : ''}
                            </button>

                            {(canMerge || canSplit) && (
                                <button
                                    onClick={() => setMergeCols(new Set())}
                                    className="px-2 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
                                >선택 해제</button>
                            )}
                        </div>

                        {/* 구분선 */}
                        <div className="w-px h-5 bg-gray-300" />

                        {/* 🔶 행별 병합 모드 */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setRowMergeMode(!rowMergeMode);
                                    if (rowMergeMode) {
                                        setRowStartCols(new Map());
                                    } else {
                                        // 모드 진입 시 기존 선택 해제
                                        setSelStart(null);
                                        setSelEnd(null);
                                        setMergeCols(new Set());
                                    }
                                }}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 ${rowMergeMode
                                    ? 'bg-blue-600 text-white hover:bg-blue-700 ring-1 ring-blue-500 shadow-md'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                title="행별로 시작 위치를 클릭하여 병합 범위를 지정합니다"
                            >
                                <MousePointerClick className="w-3.5 h-3.5" />
                                행별 병합 {rowMergeMode ? 'ON' : ''}
                            </button>

                            {rowMergeMode && canRowMerge && (
                                <>
                                    <button
                                        onClick={applyRowMerge}
                                        className="px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 bg-blue-600 text-white hover:bg-blue-700 shadow-lg transition-transform active:scale-95"
                                    >
                                        <Merge className="w-3.5 h-3.5" />
                                        적용 ({rowStartCols.size}행)
                                    </button>
                                    <button
                                        onClick={() => setRowStartCols(new Map())}
                                        className="px-2 py-1.5 text-[10px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
                                    >
                                        <Undo2 className="w-3 h-3 inline mr-0.5" />초기화
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="text-[11px] text-gray-400">
                        {rowMergeMode
                            ? '셀을 클릭하면 해당 행의 시작 위치로 마킹됩니다 (→ 행 끝까지 자동 선택)'
                            : '키보드: ←↑→↓ 이동 · Enter 편집 · Ctrl+Z/Y 되돌리기 · Ctrl+클릭 병합 선택'
                        }
                    </div>
                </div>

                {/* ─── 병합/분리 선택 표시 ─── */}
                {canMerge && (
                    <div className="px-5 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-600 flex items-center gap-2">
                        <Columns3 className="w-3.5 h-3.5" />
                        <span>
                            병합 대상: {[...mergeCols].sort((a, b) => a - b).map(ci => <span key={ci} className="inline-block px-1.5 py-0.5 bg-gray-200 rounded text-[10px] font-bold mr-1">{colLabels[ci] || String.fromCharCode(65 + ci)}</span>)}
                            → 구분자 "{MERGE_SEPARATORS.find(s => s.char === mergeSeparator)?.label || mergeSeparator}"로 합침
                        </span>
                    </div>
                )}
                {rowMergeMode && canRowMerge && (
                    <div className="px-5 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-600 flex items-center gap-2">
                        <MousePointerClick className="w-3.5 h-3.5" />
                        <span>
                            행별 병합: {rowStartCols.size}개 행 시작 위치 지정됨
                            {rowStartCols.size > 0 && (
                                <span className="ml-2 text-[10px] text-gray-400">
                                    (예: {(() => {
                                        const entries = [...rowStartCols.entries()].slice(0, 3);
                                        return entries.map(([r, c]) => `${r + 1}행→${String.fromCharCode(65 + c)}열`).join(', ');
                                    })()}{rowStartCols.size > 3 ? ' ...' : ''})
                                </span>
                            )}
                            → 구분자 "{MERGE_SEPARATORS.find(s => s.char === mergeSeparator)?.label || mergeSeparator}"로 합침
                        </span>
                    </div>
                )}
                {canSplit && (
                    <div className="px-5 py-1.5 bg-gray-50 border-b border-gray-200 text-[11px] text-gray-600 flex items-center gap-2">
                        <Grid3x3 className="w-3.5 h-3.5" />
                        <span>
                            분리 대상: {[...mergeCols].map(ci => <span key={ci} className="inline-block px-1.5 py-0.5 bg-gray-200 rounded text-[10px] font-bold mr-1">{colLabels[ci] || String.fromCharCode(65 + ci)}</span>)}
                            → 구분자 "{MERGE_SEPARATORS.find(s => s.char === mergeSeparator)?.label || mergeSeparator}"로 분리
                        </span>
                    </div>
                )}

                {/* ─── 테이블 ─── */}
                <div ref={scrollContainerRef} className="flex-1 overflow-auto" style={{ minHeight: '300px', maxHeight: 'calc(85vh - 200px)' }}
                    onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
                >
                    <table
                        className="w-full border-collapse"
                        style={{ fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: '12px' }}
                        onMouseLeave={() => { if (isDragging) setIsDragging(false); }}
                    >
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <th
                                    className="text-center text-[10px] font-bold text-gray-400 select-none cursor-pointer hover:bg-gray-200"
                                    style={{
                                        minWidth: '36px', maxWidth: '36px', padding: '6px 2px',
                                        background: '#f1f5f9',
                                        borderBottom: '2px solid #cbd5e1',
                                        borderRight: '1px solid #e2e8f0',
                                        position: 'sticky', left: 0, zIndex: 11
                                    }}
                                    onClick={selectAll}
                                    title="전체 선택"
                                >#</th>
                                {Array.from({ length: colCount }, (_, ci) => {
                                    const isMergeSelected = mergeCols.has(ci);
                                    return (
                                        <th
                                            key={ci}
                                            className={`text-left text-[11px] font-bold select-none truncate cursor-pointer transition-colors hover:bg-gray-100`}
                                            style={{
                                                padding: '6px 8px',
                                                background: isMergeSelected ? '#2563eb' : '#f1f5f9', // 진한 파랑 배경
                                                borderBottom: isMergeSelected ? '3px solid #1d4ed8' : '2px solid #cbd5e1',
                                                borderRight: '1px solid #e2e8f0',
                                                minWidth: '80px', maxWidth: '300px',
                                                color: isMergeSelected ? '#ffffff' : '#374151' // 흰색 텍스트
                                            }}
                                            onClick={(e) => {
                                                if (rowMergeMode) {
                                                    setAllRowStarts(ci);
                                                } else {
                                                    handleColHeaderClick(ci, e);
                                                }
                                            }}
                                            title={rowMergeMode
                                                ? `${colLabels[ci] || ''} — 클릭: 전체 행의 시작 위치를 이 열로 설정`
                                                : `${colLabels[ci] || ''} — 클릭: 열 선택 | Ctrl+클릭: 병합 선택`
                                            }
                                        >
                                            <span className="flex items-center gap-1">
                                                {isMergeSelected && <Check className="w-3 h-3 text-white" />}
                                                <span className={`w-4 h-4 rounded text-[9px] flex items-center justify-center font-bold ${isMergeSelected ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                    {String.fromCharCode(65 + ci)}
                                                </span>
                                                <span className={isMergeSelected ? 'text-white font-bold' : 'text-gray-500'}>
                                                    {colLabels[ci] || (ci === 0 ? columnName : `분리 ${ci}`)}
                                                </span>
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Virtual scroll spacer (top) */}
                            {visibleRange.start > 0 && (
                                <tr><td colSpan={colCount + 1} style={{ height: visibleRange.start * ROW_HEIGHT, padding: 0, border: 'none' }} /></tr>
                            )}
                            {cells.slice(visibleRange.start, visibleRange.end + 1).map((row, idx) => {
                                const ri = visibleRange.start + idx;
                                return (
                                    <tr key={ri} className="group">
                                        <td
                                            className="text-center text-[10px] text-gray-400 select-none"
                                            style={{
                                                padding: '2px 2px', background: '#f8fafc',
                                                borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #e2e8f0',
                                                position: 'sticky', left: 0, zIndex: 5,
                                                height: ROW_HEIGHT
                                            }}
                                        >{ri + 1}</td>
                                        {row.map((val, ci) => {
                                            const selected = isSelected(ri, ci);
                                            const editing = editCell && editCell.r === ri && editCell.c === ci;
                                            const isMergeCol = mergeCols.has(ci);
                                            const isRowHighlight = isRowMergeHighlighted(ri, ci);
                                            const isRowStart = isRowMergeStart(ri, ci);
                                            const isFocused = focusCell && focusCell.r === ri && focusCell.c === ci;

                                            return (
                                                <td
                                                    key={ci}
                                                    className={`relative transition-colors ${rowMergeMode ? 'cursor-pointer' : 'cursor-default'
                                                        } ${selected && !rowMergeMode ? '' : 'hover:bg-gray-50'}`}
                                                    style={{
                                                        padding: editing ? '0' : '2px 8px',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        borderRight: '1px solid #f1f5f9',
                                                        borderLeft: isRowStart ? '3px solid #2563eb' : undefined, // 시작 위치: 파란색 테두리
                                                        background: editing ? '#f9fafb'
                                                            : isRowHighlight ? (isRowStart ? '#dbeafe' : '#eff6ff') // 시작: 파랑-100, 범위: 파랑-50
                                                                : selected ? '#dbeafe' // 드래그 선택 (진한 파랑)
                                                                    : isMergeCol ? '#eff6ff' // 병합 열 (연한 파랑)
                                                                        : 'white',
                                                        maxWidth: '300px', minWidth: '80px',
                                                        lineHeight: '22px', whiteSpace: 'nowrap',
                                                        height: ROW_HEIGHT,
                                                        overflow: 'hidden', textOverflow: 'ellipsis',
                                                        userSelect: 'none',
                                                        outline: isFocused ? '2px solid #3b82f6'
                                                            : isRowHighlight ? '1px solid #93c5fd' // 병합 범위: 파랑-300
                                                                : selected ? '1px solid #cbd5e1' : 'none',
                                                        outlineOffset: isFocused ? '-2px' : undefined,
                                                    }}
                                                    onMouseDown={(e) => handleMouseDown(ri, ci, e)}
                                                    onMouseEnter={() => handleMouseEnter(ri, ci)}
                                                    onDoubleClick={() => handleDoubleClick(ri, ci)}
                                                    title={val}
                                                >
                                                    {editing ? (
                                                        <input
                                                            ref={editInputRef}
                                                            type="text"
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            onBlur={commitEdit}
                                                            onKeyDown={handleEditKeyDown}
                                                            onCompositionStart={() => { isComposingRef.current = true; }}
                                                            onCompositionEnd={() => { isComposingRef.current = false; }}
                                                            className="w-full h-full border-none outline-none text-[12px] px-2 py-1"
                                                            style={{ fontFamily: 'inherit', background: '#f9fafb', lineHeight: '22px' }}
                                                        />
                                                    ) : (
                                                        <span className={
                                                            isRowHighlight ? 'text-gray-800 font-medium'
                                                                : isMergeCol ? 'text-blue-900 font-medium'
                                                                    : 'text-gray-700'
                                                        }>
                                                            {val}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {/* Virtual scroll spacer (bottom) */}
                            {visibleRange.end < cells.length - 1 && (
                                <tr><td colSpan={colCount + 1} style={{ height: (cells.length - 1 - visibleRange.end) * ROW_HEIGHT, padding: 0, border: 'none' }} /></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ─── 토스트 ─── */}
                {toastMsg && (
                    <div className="absolute top-16 left-1/2 -translate-x-1/2 px-4 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg shadow-xl z-[60]"
                        style={{ animation: 'fadeIn 0.15s ease' }}>
                        {toastMsg}
                    </div>
                )}

                {/* ─── 하단 ─── */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <span className="text-[11px] text-gray-400">
                        {colCount > 1
                            ? `✓ ${colCount}개 열 — 적용 시 분리된 열이 그리드에 추가됩니다`
                            : `⚠️ 구분자로 분리할 수 없습니다 — 다른 구분자를 선택하세요`}
                    </span>
                    <div className="flex gap-2">
                        <button onClick={onClose}
                            className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition border border-gray-200">
                            취소
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={colCount <= 1}
                            className={`px-4 py-2 text-xs font-bold rounded-lg transition shadow-sm flex items-center gap-1.5 ${colCount > 1 ? 'bg-gray-700 text-white hover:bg-gray-800' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            <Plus className="w-3.5 h-3.5" />
                            분리 적용 ({colCount}열)
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GridTextEditor;
